import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronLeft, Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { compressImageFile, approxBase64Bytes } from "../utils/imageCompress";

/**
 * Trade-linked KYC submission for P2P (India + Nigeria).
 * Buyer uploads ID docs + Selfie + Phone + (Nigeria only) BVN/NIN numbers.
 * Verification happens before bank/payment details are revealed.
 */
const P2PKycPage = () => {
  const { tradeId } = useParams();
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const api = authAxios();

  const [tradeCountry, setTradeCountry] = useState("india");
  const [aadharFront, setAadharFront] = useState("");
  const [aadharBack, setAadharBack] = useState("");
  const [selfie, setSelfie] = useState("");
  const [phone, setPhone] = useState("");
  const [bvn, setBvn] = useState("");
  const [nin, setNin] = useState("");
  const [busy, setBusy] = useState(false);
  const [compressing, setCompressing] = useState(null); // tracks which field is being compressed

  useEffect(() => {
    api.get(`/p2p/trades/${tradeId}`).then((r) => {
      const c = r.data?.trade?.country || "india";
      setTradeCountry(c);
    }).catch(() => {});
    // eslint-disable-next-line
  }, [tradeId]);

  const isNigeria = tradeCountry === "nigeria";

  const handleFile = (setter, label) => async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressing(label);
    try {
      const originalKb = Math.round(f.size / 1024);
      const dataUrl = await compressImageFile(f, { targetMaxBytes: 110 * 1024, maxWidth: 1024, maxHeight: 1024, initialQuality: 0.7, minQuality: 0.3 });
      const finalKb = Math.round(approxBase64Bytes(dataUrl) / 1024);
      setter(dataUrl);
      toast.success(`${label}: ${originalKb} KB → ${finalKb} KB`);
    } catch (err) {
      toast.error("Image compress failed: " + err.message);
    }
    setCompressing(null);
  };

  const submit = async () => {
    if (!aadharFront || !aadharBack || !selfie || !phone) {
      toast.error("All fields are required");
      return;
    }
    if (isNigeria) {
      if (!/^\d{11}$/.test(bvn)) { toast.error("BVN must be 11 digits"); return; }
      if (!/^\d{11}$/.test(nin)) { toast.error("NIN must be 11 digits"); return; }
      if (!/^\d{10,14}$/.test(phone)) { toast.error("Enter a valid Nigerian phone"); return; }
    } else {
      if (!/^\d{10}$/.test(phone)) { toast.error("Phone must be 10 digits"); return; }
    }
    setBusy(true);
    try {
      await api.post(`/p2p/trades/${tradeId}/kyc`, {
        aadhar_front_b64: aadharFront,
        aadhar_back_b64: aadharBack,
        selfie_b64: selfie,
        phone,
        bvn_number: isNigeria ? bvn : undefined,
        nin_number: isNigeria ? nin : undefined,
      });
      toast.success("KYC submitted for verification.");
      navigate(`/p2p/trade/${tradeId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border flex items-center px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1 mr-2" data-testid="kyc-back-btn">
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-lg font-bold flex items-center gap-2">
          <ShieldCheck size={20} className="text-primary" /> KYC Verification
        </h1>
      </div>

      <div className="px-4 py-4 space-y-4">
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-xs flex gap-2">
          <AlertCircle size={16} className="text-yellow-400 shrink-0 mt-0.5" />
          <div>
            {isNigeria
              ? "Nigeria P2P requires BVN + NIN + Selfie verification before payment details are revealed. Verification takes 5–30 minutes; once approved you won't need to re-verify."
              : "India me sirf bank-transfer P2P allowed hai. Bank account dekhne se pehle Aadhar verification mandatory hai. Verification process me thoda time lagega (5–30 min). Ek baar verify hone par future trades me dobara nahi karna padega."}
          </div>
        </div>

        <FileBlock
          label={isNigeria ? "BVN Slip / Government ID — Front" : "Aadhar Front Side"}
          value={aadharFront}
          onChange={handleFile(setAadharFront, isNigeria ? "BVN Slip Front" : "Aadhar Front")}
          testId="kyc-aadhar-front"
          loading={compressing === (isNigeria ? "BVN Slip Front" : "Aadhar Front")}
        />
        <FileBlock
          label={isNigeria ? "NIN Slip / Government ID — Back" : "Aadhar Back Side"}
          value={aadharBack}
          onChange={handleFile(setAadharBack, isNigeria ? "NIN Slip" : "Aadhar Back")}
          testId="kyc-aadhar-back"
          loading={compressing === (isNigeria ? "NIN Slip" : "Aadhar Back")}
        />
        <FileBlock
          label={isNigeria ? "Selfie holding your BVN/NIN slip" : "Selfie with Aadhar in hand"}
          value={selfie}
          onChange={handleFile(setSelfie, "Selfie")}
          testId="kyc-selfie"
          loading={compressing === "Selfie"}
        />

        {isNigeria && (
          <>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">BVN (11 digits)</label>
              <input
                value={bvn}
                onChange={(e) => setBvn(e.target.value.replace(/\D/g, "").slice(0, 11))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                placeholder="22XXXXXXXXX"
                data-testid="kyc-bvn"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">NIN (11 digits)</label>
              <input
                value={nin}
                onChange={(e) => setNin(e.target.value.replace(/\D/g, "").slice(0, 11))}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
                placeholder="12345678901"
                data-testid="kyc-nin"
              />
            </div>
          </>
        )}

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{isNigeria ? "Phone Number" : "Phone Number (10 digits)"}</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, isNigeria ? 14 : 10))}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm"
            placeholder={isNigeria ? "0801XXXXXXX" : "9XXXXXXXXX"}
            data-testid="kyc-phone"
          />
        </div>

        <button
          onClick={submit}
          disabled={busy || compressing !== null}
          className="w-full py-3 bg-primary text-primary-foreground rounded-xl font-bold disabled:opacity-50"
          data-testid="kyc-submit-btn"
        >
          {busy ? <Loader2 className="animate-spin inline" /> : "Submit for Verification"}
        </button>
      </div>
    </div>
  );
};

const FileBlock = ({ label, value, onChange, testId, loading }) => (
  <div className="bg-card border border-border rounded-xl p-3 space-y-2 relative">
    <label className="text-xs text-muted-foreground block">{label}</label>
    <input type="file" accept="image/*" onChange={onChange} disabled={loading} className="text-xs w-full" data-testid={testId} />
    {loading && <CompressionOverlay label={label} />}
    {value && !loading && <img src={value} alt={label} className="rounded-lg max-h-40 mx-auto" />}
  </div>
);

const CompressionOverlay = ({ label }) => {
  const [dots, setDots] = React.useState(1);
  const [elapsed, setElapsed] = React.useState(0);
  React.useEffect(() => {
    const dotIv = setInterval(() => setDots((d) => (d % 3) + 1), 350);
    const timeIv = setInterval(() => setElapsed((s) => s + 0.1), 100);
    return () => { clearInterval(dotIv); clearInterval(timeIv); };
  }, []);
  return (
    <div className="absolute inset-0 bg-background/95 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center gap-2 z-10" data-testid="compression-overlay">
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-yellow-500/30 border-t-yellow-400 animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-yellow-400">
          {elapsed.toFixed(1)}s
        </div>
      </div>
      <div className="text-sm font-semibold text-yellow-400">
        Compressing {label}{".".repeat(dots)}
      </div>
      <div className="text-[10px] text-muted-foreground text-center px-2">
        Reducing size to ~100 KB for faster upload<br />
        <span className="text-yellow-500/80">Please wait, do not close...</span>
      </div>
      <div className="flex gap-1 mt-1">
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce"></span>
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.15s" }}></span>
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }}></span>
      </div>
    </div>
  );
};

export default P2PKycPage;
